import React, { useState, useEffect } from 'react';
import { Network, TrendingUp, GitBranch, AlertTriangle, Target, Plus, Brain } from 'lucide-react';
import { DependencyGraphNode, DependencyGraphEdge, GoalDependency } from '../../types';
import { apiService } from '../../services/api';
import { DependencyGraphView } from './DependencyGraphView';
import { DependencyListView } from './DependencyListView';
import { SynergyAnalysis } from './SynergyAnalysis';
import { ImpactSimulator } from './ImpactSimulator';
import { CreateDependencyModal } from './CreateDependencyModal';
import AIDependencyAnalyzer from './AIDependencyAnalyzer';

interface DependencyVisualizationProps {
  projectId?: string;
}

export const DependencyVisualization: React.FC<DependencyVisualizationProps> = ({ projectId }) => {
  const [nodes, setNodes] = useState<DependencyGraphNode[]>([]);
  const [edges, setEdges] = useState<DependencyGraphEdge[]>([]);
  const [dependencies, setDependencies] = useState<GoalDependency[]>([]);
  const [activeView, setActiveView] = useState<'graph' | 'list' | 'synergy' | 'impact'>('graph');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAIAnalyzer, setShowAIAnalyzer] = useState(false);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch dependency graph data
      const [graphResponse, dependenciesResponse] = await Promise.all([
        apiService.getDependencyGraph(),
        apiService.getDependencies(projectId ? { projectId } : {})
      ]);
      
      setNodes(graphResponse.nodes);
      setEdges(graphResponse.edges);
      setDependencies(dependenciesResponse.dependencies);
    } catch (err) {
      console.error('Failed to fetch dependency data:', err);
      setError('Failed to load dependency visualization');
    } finally {
      setLoading(false);
    }
  };

  const handleDependencyCreated = () => {
    setShowCreateModal(false);
    fetchData();
  };

  const handleAIDependenciesCreated = () => {
    setShowAIAnalyzer(false);
    fetchData();
  };

  const handleDependencyUpdated = () => {
    fetchData();
  };

  const handleDependencyDeleted = () => {
    fetchData();
  };

  const getViewIcon = (view: string) => {
    switch (view) {
      case 'graph':
        return <Network className="w-4 h-4" />;
      case 'list':
        return <GitBranch className="w-4 h-4" />;
      case 'synergy':
        return <TrendingUp className="w-4 h-4" />;
      case 'impact':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Network className="w-4 h-4" />;
    }
  };

  const getFilteredData = () => {
    if (!projectId) return { nodes, edges, dependencies };
    
    // Filter data for specific project
    const projectNodes = nodes.filter(node => node.projectId === projectId);
    const projectNodeIds = new Set(projectNodes.map(node => node.id));
    const projectEdges = edges.filter(edge => 
      projectNodeIds.has(edge.source) && projectNodeIds.has(edge.target)
    );
    const projectDependencies = dependencies.filter(dep => 
      projectNodeIds.has(dep.sourceGoalId) && projectNodeIds.has(dep.targetGoalId)
    );
    
    return { 
      nodes: projectNodes, 
      edges: projectEdges, 
      dependencies: projectDependencies 
    };
  };

  const { nodes: filteredNodes, edges: filteredEdges, dependencies: filteredDependencies } = getFilteredData();

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center text-red-600">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Network className="w-6 h-6 text-blue-600" />
            Cross-Project Dependencies
            {projectId && (
              <span className="text-sm font-normal text-gray-500">
                (Project Scope)
              </span>
            )}
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAIAnalyzer(true)}
              className="btn-secondary flex items-center gap-2"
              disabled={!projectId}
              title={!projectId ? "AI Analysis is only available for specific projects" : ""}
            >
              <Brain className="w-4 h-4" />
              AI Analyze
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Dependency
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Target className="w-4 h-4" />
              Total Goals
            </div>
            <div className="text-2xl font-bold text-gray-900">{filteredNodes.length}</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <GitBranch className="w-4 h-4" />
              Dependencies
            </div>
            <div className="text-2xl font-bold text-gray-900">{filteredEdges.length}</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <AlertTriangle className="w-4 h-4" />
              Critical
            </div>
            <div className="text-2xl font-bold text-red-600">
              {filteredDependencies.filter(d => d.isCritical).length}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Network className="w-4 h-4" />
              Projects
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {new Set(filteredNodes.map(n => n.projectId)).size}
            </div>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          {[
            { id: 'graph', label: 'Graph View' },
            { id: 'list', label: 'List View' },
            { id: 'synergy', label: 'Synergies' },
            { id: 'impact', label: 'Impact Analysis' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors flex-1 justify-center ${
                activeView === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {getViewIcon(tab.id)}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active View Content */}
      <div>
        {activeView === 'graph' && (
          <DependencyGraphView
            nodes={filteredNodes}
            edges={filteredEdges}
            onRefresh={fetchData}
          />
        )}

        {activeView === 'list' && (
          <DependencyListView
            dependencies={filteredDependencies}
            onUpdate={handleDependencyUpdated}
            onDelete={handleDependencyDeleted}
          />
        )}

        {activeView === 'synergy' && (
          <SynergyAnalysis />
        )}

        {activeView === 'impact' && (
          <ImpactSimulator 
            availableGoals={filteredNodes}
          />
        )}
      </div>

      {/* Create Dependency Modal */}
      {showCreateModal && (
        <CreateDependencyModal
          availableGoals={nodes}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleDependencyCreated}
        />
      )}

      {/* AI Dependency Analyzer Modal */}
      {showAIAnalyzer && projectId && (
        <AIDependencyAnalyzer
          projectId={projectId}
          onClose={() => setShowAIAnalyzer(false)}
          onDependenciesCreated={handleAIDependenciesCreated}
        />
      )}
    </div>
  );
};

